import { useForm, Link } from '@inertiajs/react';
import { Users, Mail, Loader2 } from 'lucide-react';
import { Text } from '@/components/design-system/Text';
import { Button } from '@/components/ui/button';
import { Section } from '@/components/design-system/Section';
import { Banner } from '@/components/design-system/Banner';

interface Invitation {
  id: string;
  token: string;
  organization_name: string;
  invited_by: string;
  expires_at: string;
  email: string;
  project_name: string | null;
  project_scoped: boolean;
}

interface Props {
  invitation: Invitation;
  [key: string]: any;
}

export default function Show({ invitation }: Props) {
  const { post, processing } = useForm({});

  const handleAccept = () => {
    post(`/convites/${invitation.token}/aceitar`);
  };

  const expiresAt = new Date(invitation.expires_at);
  const isExpired = expiresAt < new Date();

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100">
            <Users className="h-8 w-8 text-blue-600" />
          </div>
          <Text as="h2" size="display-small" align="center" className="mt-6">
            {invitation.project_scoped ? 'Convite para projeto' : 'Convite para organização'}
          </Text>
          <Text size="body" color="subdued" align="center" className="mt-2">
            {invitation.project_scoped ? 'Você recebeu acesso a um projeto da StationZero' : 'Você foi convidado para participar de uma organização'}
          </Text>
        </div>

        <Section>
          <div className="space-y-6">
            <div className="text-center">
              <Text as="h3" size="heading-small" align="center" className="mb-2">
                {invitation.project_name || invitation.organization_name}
              </Text>
              <div className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <Text size="body-small" color="subdued">Convidado por {invitation.invited_by}</Text>
              </div>
            </div>

            <Banner status="info" title="Informações do Convite">
              <div className="space-y-1">
                <Text size="body-small">Para: {invitation.email}</Text>
                <Text size="body-small">
                  Expira em: {expiresAt.toLocaleDateString('pt-BR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Text>
              </div>
            </Banner>

            {isExpired ? (
              <Banner status="critical" title="Este convite expirou">
                <Text size="body-small">
                  Entre em contato com o proprietário da organização para solicitar um novo convite.
                </Text>
              </Banner>
            ) : (
              <div className="space-y-4">
                <Banner status="success" title="Ao aceitar este convite, você poderá:">
                  {invitation.project_scoped ? <ul className="space-y-1">
                    <li><Text size="body-small">• Acessar somente este projeto</Text></li>
                    <li><Text size="body-small">• Usar apenas os módulos liberados para você</Text></li>
                    <li><Text size="body-small">• Colaborar conforme a permissão definida no projeto</Text></li>
                  </ul> : <ul className="space-y-1">
                    <li><Text size="body-small">• Acessar recursos compartilhados da organização</Text></li>
                    <li><Text size="body-small">• Colaborar com outros membros</Text></li>
                    <li><Text size="body-small">• Criar recursos em nome da organização</Text></li>
                    <li><Text size="body-small">• Participar das atividades da organização</Text></li>
                  </ul>}
                </Banner>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleAccept}
                    disabled={processing}
                  >
                    {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                    {processing ? 'Aceitando...' : 'Aceitar Convite'}
                  </Button>

                  <Link href="/" className="flex-1">
                    <Button type="button" variant="outline" className="w-full">
                      Recusar
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            <div className="text-center">
              <Text size="caption" color="muted">
                Se você não esperava este convite, pode ignorar este email com segurança.
              </Text>
            </div>
          </div>
        </Section>

        <div className="flex items-center justify-center gap-2">
          <img src="/logo-dark.png" alt="StationZero" className="h-5" />
          <span className="text-sm text-muted-foreground">Transforme seu e-commerce com IA</span>
        </div>
      </div>
    </div>
  );
}
